import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  PageSection,
  Spinner,
  TextInput
} from "@patternfly/react-core";

import { apiGet, apiPost } from "../../app/api";
import { locationLabel, productLabel } from "../../display";
import type { Location, Product, VendorsPayload } from "../../types";
import { StandardDataTable, type StandardColumn } from "../../components/StandardDataTable";

type AdminProduct = Product & {
  active?: number;
  food_non_food?: string;
  reorder_point?: number;
  shelf_life_days?: number;
  mol_shared?: number;
  mol_exclusive?: number;
  night_report?: number;
};

type AdminLocation = Location & {
  city?: string;
  active?: number;
};

type AdminVendor = {
  id: number;
  vendor_name: string;
  address?: string;
  phone?: string;
  mobile?: string;
  email?: string;
  tin?: string;
  active?: number;
};

type Driver = {
  id: number;
  driver_name: string;
  phone?: string;
  active?: number;
  created_by_name?: string;
};

type ProductForm = {
  id: string;
  sku: string;
  report_name: string;
  name: string;
  product_type: string;
  food_non_food: string;
  category: string;
  uom: string;
  bundle_box: string;
  reorder_point: string;
  shelf_life_days: string;
  mol_shared: string;
  mol_exclusive: string;
  night_report: string;
  active: string;
};

type LocationForm = {
  id: string;
  code: string;
  name: string;
  city: string;
  location_type: string;
  active: string;
};

type VendorForm = {
  vendor_name: string;
  tin: string;
  address: string;
  phone: string;
  mobile: string;
  email: string;
  product_id: string;
  minimum_lot_size: string;
  unit_cost: string;
};

type DriverForm = {
  id: string;
  driver_name: string;
  phone: string;
  active: string;
};

const emptyProduct: ProductForm = {
  id: "",
  sku: "",
  report_name: "",
  name: "",
  product_type: "ingredient",
  food_non_food: "",
  category: "",
  uom: "",
  bundle_box: "",
  reorder_point: "0",
  shelf_life_days: "",
  mol_shared: "0",
  mol_exclusive: "0",
  night_report: "0",
  active: "1"
};

const emptyLocation: LocationForm = {
  id: "",
  code: "",
  name: "",
  city: "",
  location_type: "branch",
  active: "1"
};

const emptyVendor: VendorForm = {
  vendor_name: "",
  tin: "",
  address: "",
  phone: "",
  mobile: "",
  email: "",
  product_id: "",
  minimum_lot_size: "1",
  unit_cost: "0"
};

const emptyDriver: DriverForm = {
  id: "",
  driver_name: "",
  phone: "",
  active: "1"
};

function boolText(value: number | undefined): string {
  return Number(value ?? 1) ? "Yes" : "No";
}

export function SetupWorkspace({ csrfToken }: { csrfToken: string }) {
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [locations, setLocations] = useState<AdminLocation[]>([]);
  const [vendors, setVendors] = useState<AdminVendor[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [activePanel, setActivePanel] = useState<"items" | "locations" | "vendors" | "drivers">("items");
  const [productForm, setProductForm] = useState<ProductForm>(emptyProduct);
  const [locationForm, setLocationForm] = useState<LocationForm>(emptyLocation);
  const [vendorForm, setVendorForm] = useState<VendorForm>(emptyVendor);
  const [driverForm, setDriverForm] = useState<DriverForm>(emptyDriver);
  const [modal, setModal] = useState<"product" | "location" | "vendor" | "driver" | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ variant: "success" | "danger"; title: string } | null>(null);

  const loadSetup = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const [productPayload, locationPayload, vendorPayload, driverPayload] = await Promise.all([
        apiGet<{ products?: AdminProduct[] }>("/api/admin/products"),
        apiGet<{ locations?: AdminLocation[] }>("/api/admin/locations"),
        apiGet<VendorsPayload>("/api/vendors"),
        apiGet<{ drivers?: Driver[] }>("/api/drivers")
      ]);
      setProducts(productPayload.products || []);
      setLocations(locationPayload.locations || []);
      setVendors((vendorPayload.vendors || []) as AdminVendor[]);
      setDrivers(driverPayload.drivers || []);
      setVendorForm(current => current.product_id ? current : { ...current, product_id: String((productPayload.products || [])[0]?.id || "") });
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Master Setup could not be loaded." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSetup();
  }, [loadSetup]);

  const updateProduct = (key: keyof ProductForm, value: string): void => setProductForm(current => ({ ...current, [key]: value }));
  const updateLocation = (key: keyof LocationForm, value: string): void => setLocationForm(current => ({ ...current, [key]: value }));
  const updateVendor = (key: keyof VendorForm, value: string): void => setVendorForm(current => ({ ...current, [key]: value }));
  const updateDriver = (key: keyof DriverForm, value: string): void => setDriverForm(current => ({ ...current, [key]: value }));

  const openProduct = (row?: AdminProduct): void => {
    setProductForm(row ? {
      id: String(row.id || ""),
      sku: row.sku || "",
      report_name: row.report_name || row.name || "",
      name: row.name || "",
      product_type: row.product_type || "ingredient",
      food_non_food: row.food_non_food || "",
      category: row.category || "",
      uom: row.uom || "",
      bundle_box: row.bundle_box || "",
      reorder_point: String(row.reorder_point ?? 0),
      shelf_life_days: String(row.shelf_life_days ?? ""),
      mol_shared: String(Number(row.mol_shared || 0)),
      mol_exclusive: String(Number(row.mol_exclusive || 0)),
      night_report: String(Number(row.night_report || 0)),
      active: String(Number(row.active ?? 1))
    } : emptyProduct);
    setModal("product");
  };

  const openLocation = (row?: AdminLocation): void => {
    setLocationForm(row ? {
      id: String(row.id || ""),
      code: row.code || "",
      name: row.name || "",
      city: row.city || "",
      location_type: row.location_type || "branch",
      active: String(Number(row.active ?? 1))
    } : emptyLocation);
    setModal("location");
  };

  const openVendor = (row?: AdminVendor): void => {
    setVendorForm(row ? {
      vendor_name: row.vendor_name || "",
      tin: row.tin || "",
      address: row.address || "",
      phone: row.phone || "",
      mobile: row.mobile || "",
      email: row.email || "",
      product_id: String(products[0]?.id || ""),
      minimum_lot_size: "1",
      unit_cost: "0"
    } : { ...emptyVendor, product_id: String(products[0]?.id || "") });
    setModal("vendor");
  };

  const openDriver = (row?: Driver): void => {
    setDriverForm(row ? {
      id: String(row.id || ""),
      driver_name: row.driver_name || "",
      phone: row.phone || "",
      active: String(Number(row.active ?? 1))
    } : emptyDriver);
    setModal("driver");
  };

  const saveProduct = async (): Promise<void> => {
    setSaving(true);
    setMessage(null);
    try {
      await apiPost<{ ok: boolean }>("/api/products", {
        ...productForm,
        reorder_point: Number(productForm.reorder_point || 0),
        shelf_life_days: productForm.shelf_life_days ? Number(productForm.shelf_life_days) : "",
        active: Number(productForm.active),
        mol_shared: Number(productForm.mol_shared),
        mol_exclusive: Number(productForm.mol_exclusive),
        night_report: Number(productForm.night_report)
      }, csrfToken);
      setMessage({ variant: "success", title: "Inventory item saved." });
      setModal(null);
      await loadSetup();
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Inventory item could not be saved." });
    } finally {
      setSaving(false);
    }
  };

  const saveLocation = async (): Promise<void> => {
    setSaving(true);
    setMessage(null);
    try {
      await apiPost<{ ok: boolean }>("/api/locations", { ...locationForm, active: Number(locationForm.active) }, csrfToken);
      setMessage({ variant: "success", title: "Location saved." });
      setModal(null);
      await loadSetup();
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Location could not be saved." });
    } finally {
      setSaving(false);
    }
  };

  const saveVendor = async (): Promise<void> => {
    setSaving(true);
    setMessage(null);
    try {
      await apiPost<{ ok: boolean }>("/api/vendors", {
        ...vendorForm,
        product_id: Number(vendorForm.product_id),
        minimum_lot_size: Number(vendorForm.minimum_lot_size || 0),
        unit_cost: Number(vendorForm.unit_cost || 0)
      }, csrfToken);
      setMessage({ variant: "success", title: "Vendor item saved." });
      setModal(null);
      await loadSetup();
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Vendor could not be saved." });
    } finally {
      setSaving(false);
    }
  };

  const saveDriver = async (): Promise<void> => {
    setSaving(true);
    setMessage(null);
    try {
      await apiPost<{ ok: boolean }>("/api/drivers", { ...driverForm, active: Number(driverForm.active) }, csrfToken);
      setMessage({ variant: "success", title: "Driver saved." });
      setModal(null);
      await loadSetup();
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Driver could not be saved." });
    } finally {
      setSaving(false);
    }
  };

  const productColumns: StandardColumn<AdminProduct>[] = useMemo(() => [
    { key: "sku", label: "SKU", value: row => row.sku || "" },
    { key: "name", label: "Item", value: row => row.name || "", render: row => <><strong>{row.name || ""}</strong><small>{row.report_name || ""}</small></> },
    { key: "category", label: "Category", value: row => row.category || "" },
    { key: "product_type", label: "Type", value: row => row.product_type || "" },
    { key: "uom", label: "Unit", value: row => row.uom || row.bundle_box || "" },
    { key: "active", label: "Active", value: row => boolText(row.active), render: row => <Label color={Number(row.active ?? 1) ? "green" : "grey"}>{boolText(row.active)}</Label> },
    { key: "actions", label: "Actions", sortable: false, render: row => <Button variant="link" isInline onClick={() => openProduct(row)}>Edit</Button> }
  ], []);

  const locationColumns: StandardColumn<AdminLocation>[] = useMemo(() => [
    { key: "code", label: "Code", value: row => row.code || "" },
    { key: "name", label: "Location", value: row => locationLabel(row), render: row => <><strong>{row.name || ""}</strong><small>{row.code || ""}</small></> },
    { key: "city", label: "City", value: row => row.city || "" },
    { key: "location_type", label: "Type", value: row => row.location_type || "" },
    { key: "active", label: "Active", value: row => boolText(row.active), render: row => <Label color={Number(row.active ?? 1) ? "green" : "grey"}>{boolText(row.active)}</Label> },
    { key: "actions", label: "Actions", sortable: false, render: row => <Button variant="link" isInline onClick={() => openLocation(row)}>Edit</Button> }
  ], []);

  const vendorColumns: StandardColumn<AdminVendor>[] = useMemo(() => [
    { key: "vendor_name", label: "Vendor", value: row => row.vendor_name },
    { key: "phone", label: "Phone", value: row => row.phone || "" },
    { key: "mobile", label: "Mobile", value: row => row.mobile || "" },
    { key: "email", label: "Email", value: row => row.email || "" },
    { key: "tin", label: "TIN", value: row => row.tin || "" },
    { key: "actions", label: "Actions", sortable: false, render: row => <Button variant="link" isInline onClick={() => openVendor(row)}>Add supplied item</Button> }
  ], [products]);

  const driverColumns: StandardColumn<Driver>[] = useMemo(() => [
    { key: "driver_name", label: "Driver", value: row => row.driver_name },
    { key: "phone", label: "Phone", value: row => row.phone || "" },
    { key: "active", label: "Active", value: row => boolText(row.active), render: row => <Label color={Number(row.active ?? 1) ? "green" : "grey"}>{boolText(row.active)}</Label> },
    { key: "created_by_name", label: "Created By", value: row => row.created_by_name || "" },
    { key: "actions", label: "Actions", sortable: false, render: row => <Button variant="link" isInline onClick={() => openDriver(row)}>Edit</Button> }
  ], []);

  return (
    <PageSection className="pf-toj-workspace" hasBodyWrapper={false}>
      <div id="pf-setup-view">
        <Flex alignItems={{ default: "alignItemsFlexStart" }} justifyContent={{ default: "justifyContentSpaceBetween" }} className="pf-toj-workspace-title">
          <FlexItem>
            <h1>Master Setup</h1>
            <p>Maintain items, locations, vendors, and drivers without leaving the operating workspace.</p>
          </FlexItem>
          {loading && <FlexItem><Spinner size="md" aria-label="Loading Master Setup" /></FlexItem>}
        </Flex>
        {message && <Alert variant={message.variant} title={message.title} isInline className="pf-toj-inline-alert" />}
        <div className="pf-toj-section-switcher" role="group" aria-label="Master Setup sections">
          <Button variant={activePanel === "items" ? "primary" : "secondary"} onClick={() => setActivePanel("items")}>Inventory items</Button>
          <Button variant={activePanel === "locations" ? "primary" : "secondary"} onClick={() => setActivePanel("locations")}>Locations</Button>
          <Button variant={activePanel === "vendors" ? "primary" : "secondary"} onClick={() => setActivePanel("vendors")}>Vendors</Button>
          <Button variant={activePanel === "drivers" ? "primary" : "secondary"} onClick={() => setActivePanel("drivers")}>Drivers</Button>
        </div>
        <Card className="pf-toj-work-card pf-toj-primary-work-card">
          <CardBody>
            {activePanel === "items" && (
              <>
                <Flex justifyContent={{ default: "justifyContentSpaceBetween" }} className="pf-toj-table-heading">
                  <FlexItem><h2>Inventory Items</h2></FlexItem>
                  <FlexItem><Button variant="primary" onClick={() => openProduct()}>Create item</Button></FlexItem>
                </Flex>
                <StandardDataTable ariaLabel="Master inventory items" rows={products} columns={productColumns} searchPlaceholder="Search inventory items" />
              </>
            )}
            {activePanel === "locations" && (
              <>
                <Flex justifyContent={{ default: "justifyContentSpaceBetween" }} className="pf-toj-table-heading">
                  <FlexItem><h2>Locations</h2></FlexItem>
                  <FlexItem><Button variant="primary" onClick={() => openLocation()}>Add location</Button></FlexItem>
                </Flex>
                <StandardDataTable ariaLabel="Master locations" rows={locations} columns={locationColumns} searchPlaceholder="Search locations" />
              </>
            )}
            {activePanel === "vendors" && (
              <>
                <Flex justifyContent={{ default: "justifyContentSpaceBetween" }} className="pf-toj-table-heading">
                  <FlexItem><h2>Vendors</h2></FlexItem>
                  <FlexItem><Button variant="primary" onClick={() => openVendor()} isDisabled={!products.length}>Add vendor item</Button></FlexItem>
                </Flex>
                <StandardDataTable ariaLabel="Master vendors" rows={vendors} columns={vendorColumns} searchPlaceholder="Search vendors" />
              </>
            )}
            {activePanel === "drivers" && (
              <>
                <Flex justifyContent={{ default: "justifyContentSpaceBetween" }} className="pf-toj-table-heading">
                  <FlexItem><h2>Drivers</h2></FlexItem>
                  <FlexItem><Button variant="primary" onClick={() => openDriver()}>Add driver</Button></FlexItem>
                </Flex>
                <StandardDataTable ariaLabel="Master drivers" rows={drivers} columns={driverColumns} searchPlaceholder="Search drivers" />
              </>
            )}
          </CardBody>
        </Card>
      </div>

      <Modal variant={ModalVariant.large} isOpen={modal === "product"} onClose={() => setModal(null)}>
        <ModalHeader title={productForm.id ? "Edit inventory item" : "Create inventory item"} />
        <ModalBody>
          <Form className="pf-toj-form">
            <div className="pf-toj-field-row">
              <FormGroup label="SKU"><TextInput value={productForm.sku} onChange={(_event, value) => updateProduct("sku", String(value))} aria-label="SKU" /></FormGroup>
              <FormGroup label="Report name"><TextInput value={productForm.report_name} onChange={(_event, value) => updateProduct("report_name", String(value))} aria-label="Report name" /></FormGroup>
              <FormGroup label="Item name" isRequired><TextInput value={productForm.name} onChange={(_event, value) => updateProduct("name", String(value))} aria-label="Item name" /></FormGroup>
              <FormGroup label="Type"><TextInput value={productForm.product_type} onChange={(_event, value) => updateProduct("product_type", String(value))} aria-label="Product type" /></FormGroup>
              <FormGroup label="Food / Non-food"><TextInput value={productForm.food_non_food} onChange={(_event, value) => updateProduct("food_non_food", String(value))} aria-label="Food or non-food" /></FormGroup>
              <FormGroup label="Category"><TextInput value={productForm.category} onChange={(_event, value) => updateProduct("category", String(value))} aria-label="Category" /></FormGroup>
              <FormGroup label="Unit"><TextInput value={productForm.uom} onChange={(_event, value) => updateProduct("uom", String(value))} aria-label="Unit" /></FormGroup>
              <FormGroup label="Bundle / box"><TextInput value={productForm.bundle_box} onChange={(_event, value) => updateProduct("bundle_box", String(value))} aria-label="Bundle or box" /></FormGroup>
              <FormGroup label="Reorder point"><TextInput type="number" value={productForm.reorder_point} onChange={(_event, value) => updateProduct("reorder_point", String(value))} aria-label="Reorder point" /></FormGroup>
              <FormGroup label="Shelf life days"><TextInput type="number" value={productForm.shelf_life_days} onChange={(_event, value) => updateProduct("shelf_life_days", String(value))} aria-label="Shelf life days" /></FormGroup>
              <FormGroup label="MOL shared"><FormSelect value={productForm.mol_shared} onChange={(_event, value) => updateProduct("mol_shared", String(value))} aria-label="MOL shared"><FormSelectOption value="0" label="No" /><FormSelectOption value="1" label="Yes" /></FormSelect></FormGroup>
              <FormGroup label="MOL exclusive"><FormSelect value={productForm.mol_exclusive} onChange={(_event, value) => updateProduct("mol_exclusive", String(value))} aria-label="MOL exclusive"><FormSelectOption value="0" label="No" /><FormSelectOption value="1" label="Yes" /></FormSelect></FormGroup>
              <FormGroup label="Night report"><FormSelect value={productForm.night_report} onChange={(_event, value) => updateProduct("night_report", String(value))} aria-label="Night report"><FormSelectOption value="0" label="No" /><FormSelectOption value="1" label="Yes" /></FormSelect></FormGroup>
              <FormGroup label="Active"><FormSelect value={productForm.active} onChange={(_event, value) => updateProduct("active", String(value))} aria-label="Active"><FormSelectOption value="1" label="Yes" /><FormSelectOption value="0" label="No" /></FormSelect></FormGroup>
            </div>
          </Form>
        </ModalBody>
        <ModalFooter><Button variant="primary" onClick={() => void saveProduct()} isLoading={saving}>Save item</Button><Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button></ModalFooter>
      </Modal>

      <Modal variant={ModalVariant.medium} isOpen={modal === "location"} onClose={() => setModal(null)}>
        <ModalHeader title={locationForm.id ? "Edit location" : "Add location"} />
        <ModalBody>
          <Form className="pf-toj-form">
            <div className="pf-toj-field-row">
              <FormGroup label="Code" isRequired><TextInput value={locationForm.code} onChange={(_event, value) => updateLocation("code", String(value))} aria-label="Location code" /></FormGroup>
              <FormGroup label="Name" isRequired><TextInput value={locationForm.name} onChange={(_event, value) => updateLocation("name", String(value))} aria-label="Location name" /></FormGroup>
              <FormGroup label="City"><TextInput value={locationForm.city} onChange={(_event, value) => updateLocation("city", String(value))} aria-label="City" /></FormGroup>
              <FormGroup label="Type"><FormSelect value={locationForm.location_type} onChange={(_event, value) => updateLocation("location_type", String(value))} aria-label="Location type"><FormSelectOption value="branch" label="Branch" /><FormSelectOption value="commissary" label="Commissary" /></FormSelect></FormGroup>
              <FormGroup label="Active"><FormSelect value={locationForm.active} onChange={(_event, value) => updateLocation("active", String(value))} aria-label="Location active"><FormSelectOption value="1" label="Yes" /><FormSelectOption value="0" label="No" /></FormSelect></FormGroup>
            </div>
          </Form>
        </ModalBody>
        <ModalFooter><Button variant="primary" onClick={() => void saveLocation()} isLoading={saving}>Save location</Button><Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button></ModalFooter>
      </Modal>

      <Modal variant={ModalVariant.medium} isOpen={modal === "vendor"} onClose={() => setModal(null)}>
        <ModalHeader title="Vendor supplied item" />
        <ModalBody>
          <Form className="pf-toj-form">
            <div className="pf-toj-field-row">
              <FormGroup label="Vendor name" isRequired><TextInput value={vendorForm.vendor_name} onChange={(_event, value) => updateVendor("vendor_name", String(value))} aria-label="Vendor name" /></FormGroup>
              <FormGroup label="Phone"><TextInput value={vendorForm.phone} onChange={(_event, value) => updateVendor("phone", String(value))} aria-label="Vendor phone" /></FormGroup>
              <FormGroup label="Mobile"><TextInput value={vendorForm.mobile} onChange={(_event, value) => updateVendor("mobile", String(value))} aria-label="Vendor mobile" /></FormGroup>
              <FormGroup label="Email"><TextInput value={vendorForm.email} onChange={(_event, value) => updateVendor("email", String(value))} aria-label="Vendor email" /></FormGroup>
              <FormGroup label="TIN"><TextInput value={vendorForm.tin} onChange={(_event, value) => updateVendor("tin", String(value))} aria-label="Vendor TIN" /></FormGroup>
              <FormGroup label="Item supplied" isRequired><FormSelect value={vendorForm.product_id} onChange={(_event, value) => updateVendor("product_id", String(value))} aria-label="Item supplied">{products.map(product => <FormSelectOption key={product.id} value={String(product.id)} label={productLabel(product)} />)}</FormSelect></FormGroup>
              <FormGroup label="Minimum lot" isRequired><TextInput type="number" value={vendorForm.minimum_lot_size} onChange={(_event, value) => updateVendor("minimum_lot_size", String(value))} aria-label="Minimum lot" /></FormGroup>
              <FormGroup label="Unit cost"><TextInput type="number" value={vendorForm.unit_cost} onChange={(_event, value) => updateVendor("unit_cost", String(value))} aria-label="Unit cost" /></FormGroup>
              <FormGroup label="Address"><TextInput value={vendorForm.address} onChange={(_event, value) => updateVendor("address", String(value))} aria-label="Vendor address" /></FormGroup>
            </div>
          </Form>
        </ModalBody>
        <ModalFooter><Button variant="primary" onClick={() => void saveVendor()} isLoading={saving}>Save vendor item</Button><Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button></ModalFooter>
      </Modal>

      <Modal variant={ModalVariant.medium} isOpen={modal === "driver"} onClose={() => setModal(null)}>
        <ModalHeader title={driverForm.id ? "Edit driver" : "Add driver"} />
        <ModalBody>
          <Form className="pf-toj-form">
            <div className="pf-toj-field-row">
              <FormGroup label="Driver name" isRequired><TextInput value={driverForm.driver_name} onChange={(_event, value) => updateDriver("driver_name", String(value))} aria-label="Driver name" /></FormGroup>
              <FormGroup label="Phone"><TextInput value={driverForm.phone} onChange={(_event, value) => updateDriver("phone", String(value))} aria-label="Driver phone" /></FormGroup>
              <FormGroup label="Active"><FormSelect value={driverForm.active} onChange={(_event, value) => updateDriver("active", String(value))} aria-label="Driver active"><FormSelectOption value="1" label="Yes" /><FormSelectOption value="0" label="No" /></FormSelect></FormGroup>
            </div>
          </Form>
        </ModalBody>
        <ModalFooter><Button variant="primary" onClick={() => void saveDriver()} isLoading={saving}>Save driver</Button><Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button></ModalFooter>
      </Modal>
    </PageSection>
  );
}
